import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import apiClient from '@lib/api/client';
import { Link } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import {
  getPortalJobs, getPortalSchedule, getPortalInvoices, getPortalInvoicePaymentLink,
  getClientOverview,
  type PortalJob, type ScheduleEvent, type PortalInvoice,
  type ClientOverview, type ClientJobProgress, type ClientActivityItem,
} from '@lib/api/portal';
import { LargeTitleHeader, PullToRefresh, BottomSheet } from '@/components/common';

const statusColor: Record<string, string> = {
  in_progress:  'bg-blue-100 text-blue-700',
  scheduled:    'bg-amber-100 text-amber-700',
  completed:    'bg-green-100 text-green-700',
  draft:        'bg-gray-100 text-gray-600',
};

const statusLabel: Record<string, string> = {
  in_progress: 'In Progress',
  scheduled:   'Scheduled',
  completed:   'Completed',
  draft:       'Draft',
};

const fmt = (n: number) => `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const parseDateLocal = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
};

// "08:00:00" → "8:00am" (audit: the next-visit card showed raw SQL times)
const fmtTime = (t?: string | null) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}${ap}`;
};

// Company contact — real numbers (audit: "Call Us" dialled a placeholder)
const OP_PM_PHONE = '+12505100677';
const OP_PM_EMAIL = 'info@ochrepoint.ca';

// ── CR #135 helpers: progress, countdowns, plain-language dates ─────────────
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

// DATE columns come back as "YYYY-MM-DD" strings — parse at noon so the day
// never slips backwards in Pacific time (CR #115).
const dayDiff = (dateKey: string) => {
  const d = new Date(String(dateKey).slice(0, 10) + 'T12:00:00');
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - startOfToday().getTime()) / 86400000);
};

const countdownWords = (dateKey: string) => {
  const n = dayDiff(dateKey);
  if (n < 0) return 'Work is under way';
  if (n === 0) return 'Our crew is on site today';
  if (n === 1) return 'Our crew arrives tomorrow';
  if (n < 7) return `Our crew arrives in ${n} days`;
  if (n < 14) return 'Our crew arrives next week';
  return `Our crew arrives in ${Math.round(n / 7)} weeks`;
};

// "Today" / "Yesterday" / "3 days ago" / "Jul 17"
const whenWords = (dateKey: string) => {
  const n = -dayDiff(dateKey);
  if (n <= 0) return 'Today';
  if (n === 1) return 'Yesterday';
  if (n < 7) return `${n} days ago`;
  return new Date(String(dateKey).slice(0, 10) + 'T12:00:00')
    .toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

const pctDone = (j: ClientJobProgress) =>
  Number(j.steps_total) > 0 ? Math.round((Number(j.steps_done) / Number(j.steps_total)) * 100) : null;

// A progress bar a homeowner can read at a glance — always paired with words.
const ProgressBar = ({ pct }: { pct: number }) => (
  <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
    <div className="h-full rounded-full bg-[#C77D3F] transition-all" style={{ width: `${Math.max(pct, 2)}%` }} />
  </div>
);

const ACTIVITY_META: Record<ClientActivityItem['kind'], { icon: string; tint: string }> = {
  photos:    { icon: 'photo_camera',  tint: 'bg-sky-50 text-sky-600' },
  log:       { icon: 'edit_note',     tint: 'bg-[#FAF6F0] text-[#8B5A2B]' },
  task_done: { icon: 'check_circle',  tint: 'bg-green-50 text-green-600' },
  invoice:   { icon: 'receipt_long',  tint: 'bg-amber-50 text-amber-600' },
  quote:     { icon: 'request_quote', tint: 'bg-[#C77D3F]/10 text-[#C77D3F]' },
};

const activityHeadline = (a: ClientActivityItem) => {
  switch (a.kind) {
    case 'photos':    return `${a.count} new site photo${a.count === 1 ? '' : 's'}`;
    case 'log':       return 'Progress note from the crew';
    case 'task_done': return `Finished: ${a.body ?? 'a step on your project'}`;
    case 'invoice':
      if (a.label === 'paid')    return `Invoice ${a.body} — paid in full. Thank you!`;
      if (a.label === 'overdue') return `Invoice ${a.body} is past its due date`;
      if (a.label === 'partial') return `Invoice ${a.body} — part payment received`;
      return `Invoice ${a.body} was sent to you`;
    case 'quote':     return a.label === 'approved'
      ? `You approved quote ${a.body}`
      : `Quote ${a.body} is ready for you to review`;
    default:          return 'Update';
  }
};

interface PendingQuote { id: number; quote_number: string; total: number; job_title: string | null; valid_until: string | null }

// CR #153 — office announcements addressed to clients (crew_memos w/ audience)
interface ClientUpdate {
  id: number; title: string; body: string; category: string; pinned: number;
  created_at: string; job_title: string | null; created_by_name: string | null;
}
const UPDATE_META: Record<string, { icon: string; tint: string; word: string }> = {
  notice:   { icon: 'campaign',      tint: 'bg-sky-50 text-sky-600',      word: 'Notice' },
  reminder: { icon: 'notifications', tint: 'bg-amber-50 text-amber-600',  word: 'Reminder' },
  urgent:   { icon: 'priority_high', tint: 'bg-red-50 text-red-600',      word: 'Important' },
  schedule: { icon: 'event',         tint: 'bg-purple-50 text-purple-600', word: 'Schedule' },
  kudos:    { icon: 'celebration',   tint: 'bg-green-50 text-green-600',  word: 'Good news' },
};

export const ClientHomePage = () => {
  const { user } = useContext(AuthContext)!;
  const [jobs, setJobs] = useState<PortalJob[]>([]);
  interface ProgressEntry { job_id: number; job_title: string; date: string; photo_count: number; summary: string | null; photos: { id: number; url: string; caption?: string }[] }
  const [progress, setProgress] = useState<ProgressEntry[]>([]);
  const [overview, setOverview] = useState<ClientOverview | null>(null);
  const [schedule, setSchedule] = useState<ScheduleEvent[]>([]);
  const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
  const [pendingQuotes, setPendingQuotes] = useState<PendingQuote[]>([]);
  // CR #153 — "Updates from us"
  const [updates, setUpdates] = useState<ClientUpdate[]>([]);
  const [updatesOpen, setUpdatesOpen] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // #12 – one-tap pay state
  const [paying, setPaying] = useState<number | null>(null);
  // Card payments can be switched off in Settings › Financial
  const [cardPayOn, setCardPayOn] = useState(true);
  useEffect(() => {
    apiClient.get('/payments/status')
      .then(r => setCardPayOn(r.data?.data?.configured !== false))
      .catch(() => {});
  }, []);
  const [payError, setPayError] = useState('');
  // #20 – quick message/request sheet
  const [msgSheetOpen, setMsgSheetOpen] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [msgSent, setMsgSent] = useState(false);
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgError, setMsgError] = useState('');
  const msgRef = useRef<HTMLTextAreaElement>(null);
  const today = new Date().toISOString().slice(0, 10);

  // Onboarding nudge (audit: the onboarding page was write-only and skippers
  // were never asked again). Dismiss persists locally.
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  useEffect(() => {
    apiClient.get('/portal/me/progress').then(r => setProgress(r.data.data ?? [])).catch(() => {});
    if (localStorage.getItem('onboarding_nudge_dismissed') !== '1') {
      apiClient.get('/portal/me/client-profile')
        .then(r => { if (r.data.data && !Number(r.data.data.onboarding_complete)) setNeedsOnboarding(true); })
        .catch(() => {});
    }
  }, []);

  const loadAll = useCallback(async () => {
    const results = await Promise.allSettled([
      getPortalJobs().then(setJobs),
      getClientOverview().then(setOverview),
      getPortalSchedule().then(setSchedule),
      getPortalInvoices().then(setInvoices),
      apiClient.get('/portal/me/quotes').then(r =>
        setPendingQuotes((r.data.data ?? []).filter((q: PendingQuote & { status: string }) => q.status === 'sent'))),
      // CR #153 — announcements the office addressed to clients
      apiClient.get('/crew-memos/client/list').then(r => setUpdates(r.data.data ?? [])),
    ]);
    // Any failure surfaces — a silent invoice failure used to show "$0.00 all
    // paid" to a client who actually owed money (audit)
    if (results.some(r => r.status === 'rejected')) {
      setError('Some of your info didn\'t load — pull down to refresh or try again shortly.');
    } else {
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const upcoming = schedule.filter(s => s.status !== 'span' && (s.scheduled_date || '') >= today).slice(0, 2);
  const nextVisit = upcoming[0];
  // DECIMAL columns arrive as strings — Number() or the hero card shows $NaN
  // with 2+ outstanding invoices (audit)
  const outstanding = invoices.filter(i => Number(i.balance_due) > 0 && i.status !== 'paid');
  const totalOutstanding = outstanding.reduce((s, i) => s + Number(i.balance_due), 0);
  const activeJobs = jobs.filter(j => j.status === 'in_progress' || j.status === 'scheduled');

  // ── CR #135 — progress, activity and the "next work day" countdown ────────
  const progressById = new Map<number, ClientJobProgress>(
    (overview?.jobs ?? []).map(j => [Number(j.id), j])
  );
  const activity = overview?.activity ?? [];
  const sitePhotos = overview?.photos ?? [];
  // Soonest day work is expected across all projects — the earlier of a booked
  // crew visit and the next shared step the office has planned.
  const soonestJobDate = (overview?.jobs ?? [])
    .map(j => j.next_visit_date).filter(Boolean)
    .map(d => String(d).slice(0, 10)).sort()[0];
  const visitDateKey = nextVisit ? String(nextVisit.scheduled_date).slice(0, 10) : undefined;
  const heroFromVisit = !!visitDateKey && (!soonestJobDate || visitDateKey <= soonestJobDate);
  const heroJobRow = soonestJobDate
    ? (overview?.jobs ?? []).find(j => String(j.next_visit_date).slice(0, 10) === soonestJobDate)
    : undefined;
  const heroDate = heroFromVisit ? visitDateKey : soonestJobDate;
  const heroTitle = heroFromVisit ? nextVisit?.job_title : heroJobRow?.title;
  const heroStep = heroFromVisit ? undefined : heroJobRow?.next_step_title;

  const now = new Date();

  // #20 – send a quick message — REAL delivery now, honest errors (audit: the
  // endpoint didn't exist and the UI faked "Message sent!")
  const sendMessage = async () => {
    if (!msgText.trim()) return;
    setMsgBusy(true);
    setMsgError('');
    try {
      await apiClient.post('/portal/me/messages', { message: msgText.trim() });
      setMsgSent(true);
      setMsgText('');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setMsgError(ax.response?.data?.error || 'Could not send right now — please call or email us instead.');
    } finally { setMsgBusy(false); }
  };

  // #12 – one-tap pay. Same-tab navigation: window.open after an await gets
  // popup-blocked on iOS Safari (audit), and Stripe brings the client back.
  const handleQuickPay = async (inv: PortalInvoice) => {
    setPaying(inv.id);
    setPayError('');
    try {
      const { url } = await getPortalInvoicePaymentLink(inv.id);
      window.location.href = url;
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setPayError(ax.response?.data?.error || 'Online payment isn\'t available right now — you can pay by e-Transfer to billing@ochrepoint.ca.');
      setPaying(null);
    }
  };

  return (
    <PullToRefresh onRefresh={loadAll}>
    <div className="space-y-5">
      {error && <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700"><span className="material-symbols-outlined text-base">error</span>{error}</div>}

      <LargeTitleHeader
        title={`Welcome back, ${user?.username?.split(' ')[0] || 'there'}`}
        subtitle={now.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
      />

      {loading && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="h-24 rounded-xl bg-gray-100 animate-pulse" />
            <div className="h-24 rounded-xl bg-gray-100 animate-pulse" />
          </div>
          <div className="h-32 rounded-xl bg-gray-100 animate-pulse" />
        </div>
      )}

      {!loading && needsOnboarding && (
        <div className="bg-white border border-[#C77D3F]/30 rounded-xl p-4 flex items-center gap-3">
          <span className="material-symbols-outlined text-[#C77D3F] text-2xl">waving_hand</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Finish setting up your account</p>
            <p className="text-xs text-gray-500">Your contact and billing details help us serve you faster.</p>
          </div>
          <Link to="/portal/client/onboarding" className="text-xs font-bold text-white bg-[#C77D3F] rounded-lg px-3 py-2 whitespace-nowrap active:scale-95">Finish</Link>
          <button onClick={() => { localStorage.setItem('onboarding_nudge_dismissed', '1'); setNeedsOnboarding(false); }}
            className="text-gray-300 hover:text-gray-500" title="Dismiss">
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      )}

      {/* CR #153 — Updates from us: office announcements addressed to clients.
          Long ones start clamped; tap to read the whole thing. */}
      {!loading && updates.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">campaign</span>
            Updates from us
          </h2>
          <p className="text-xs text-gray-500 mb-3">Announcements and news from the OchrePoint office.</p>
          <div className="space-y-3">
            {updates.slice(0, 5).map(u => {
              const meta = UPDATE_META[u.category] || UPDATE_META.notice;
              const long = u.body.length > 180;
              const open = !!updatesOpen[u.id];
              return (
                <div key={u.id} className="flex gap-3">
                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${meta.tint}`}>
                    <span className="material-symbols-outlined text-lg">{meta.icon}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {!!u.pinned && <span className="material-symbols-outlined text-sm text-[#C77D3F] align-middle mr-0.5">push_pin</span>}
                      {u.title}
                    </p>
                    <p className={`text-sm text-gray-600 mt-0.5 whitespace-pre-wrap leading-relaxed ${long && !open ? 'line-clamp-3' : ''}`}>
                      {u.body}
                    </p>
                    {long && (
                      <button onClick={() => setUpdatesOpen(o => ({ ...o, [u.id]: !o[u.id] }))}
                        className="text-xs font-bold text-[#C77D3F] mt-0.5">
                        {open ? 'Show less' : 'Read more'}
                      </button>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {meta.word} · {whenWords(String(u.created_at).slice(0, 10))}
                      {u.job_title ? ` · ${u.job_title}` : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quotes waiting on the client — the email's "Review in Your Portal" lands here */}
      {!loading && pendingQuotes.length > 0 && (
        <div className="bg-[#C77D3F]/10 border border-[#C77D3F]/30 rounded-xl p-4">
          <h2 className="font-semibold text-[#7a4a22] mb-2 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">request_quote</span>
            Quote{pendingQuotes.length > 1 ? 's' : ''} awaiting your review
          </h2>
          <div className="space-y-2">
            {pendingQuotes.map(q => (
              <Link key={q.id} to={`/portal/client/quotes/${q.id}`}
                className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2.5 active:scale-[0.99]">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{q.job_title || `Quote ${q.quote_number}`}</p>
                  <p className="text-xs text-gray-400">{q.quote_number}{q.valid_until ? ` · valid until ${new Date(String(q.valid_until).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}</p>
                </div>
                <span className="flex items-center gap-1 text-sm font-bold text-[#C77D3F] whitespace-nowrap">
                  {fmt(Number(q.total))}
                  <span className="material-symbols-outlined text-base">arrow_forward_ios</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Daily progress from the crew — photos + plain-language summary */}
      {progress.length > 0 && (
        <div className="space-y-2.5">
          <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">photo_album</span>
            Progress updates
          </h2>
          {progress.map(p => (
            <div key={`${p.job_id}-${p.date}`} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-semibold text-gray-900 text-sm truncate">{p.job_title}</p>
                <p className="text-[11px] text-gray-400 whitespace-nowrap">
                  {new Date(p.date + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}
                </p>
              </div>
              {p.summary && <p className="text-xs text-gray-600 mt-1 leading-relaxed">{p.summary}</p>}
              <div className="flex gap-1.5 mt-2 overflow-x-auto">
                {p.photos.map(ph => (
                  <a key={ph.id} href={ph.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                    <img src={ph.url} alt={ph.caption || ''} loading="lazy"
                      className="h-20 w-20 rounded-lg object-cover border border-gray-100" />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary cards */}
      {!loading && (
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 font-medium">Active Jobs</p>
          <p className="text-2xl font-black text-[#2C1810] mt-1">{activeJobs.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">{jobs.filter(j => j.status === 'completed').length} completed</p>
        </div>
        <div className={`rounded-xl border p-4 ${totalOutstanding > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <p className="text-xs text-gray-500 font-medium">Balance Due</p>
          <p className={`text-2xl font-black mt-1 ${totalOutstanding > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {fmt(totalOutstanding)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {outstanding.length > 0 ? `${outstanding.length} invoice${outstanding.length > 1 ? 's' : ''} outstanding`
              : invoices.length > 0 ? 'All paid — thank you!' : 'No invoices yet'}
          </p>
        </div>
      </div>
      )}

      {/* Next visit — with a plain-language countdown (CR #135). The date is the
          earlier of a booked crew visit and the next step the office has planned. */}
      {heroDate && (
        <Link to="/portal/client/schedule" className="block bg-white rounded-xl border border-gray-200 p-4 hover:bg-[#FAF6F0] transition-colors">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-14 rounded-xl bg-[#C77D3F]/10 flex flex-col items-center justify-center">
              <span className="text-xs font-semibold text-[#C77D3F] uppercase tracking-wide">
                {MONTH_SHORT[parseDateLocal(heroDate).getMonth()]}
              </span>
              <span className="text-xl font-black text-[#2C1810] leading-none">
                {parseDateLocal(heroDate).getDate()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#C77D3F]">{countdownWords(heroDate)}</p>
              {heroTitle && <p className="font-semibold text-gray-900 text-sm mt-0.5">{heroTitle}</p>}
              {heroStep && <p className="text-xs text-gray-500 mt-0.5">Up next: {heroStep}</p>}
              {heroFromVisit && nextVisit?.start_time && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {fmtTime(nextVisit.start_time)}{nextVisit.end_time ? ` – ${fmtTime(nextVisit.end_time)}` : ''}
                </p>
              )}
              {heroFromVisit && nextVisit?.site_address && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">{nextVisit.site_address}</p>
              )}
            </div>
            <span className="material-symbols-outlined text-gray-300">arrow_forward_ios</span>
          </div>
        </Link>
      )}

      {/* Active jobs — now with a real progress bar per project (CR #135).
          "Steps" are the tasks the office has chosen to share with you, so the
          percentage only ever reflects work you can actually see listed. */}
      {activeJobs.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">construction</span>
            Your Projects
          </h2>
          <div className="space-y-3">
            {activeJobs.map(job => {
              const p = progressById.get(Number(job.id));
              const pct = p ? pctDone(p) : null;
              return (
                <Link key={job.id} to={`/portal/client/jobs/${job.id}`}
                  className="block py-1.5 -mx-2 px-2 rounded-lg active:bg-[#FAF6F0]">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-gray-900 truncate text-sm">{job.title}</p>
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[job.status] || 'bg-gray-100 text-gray-600'}`}>
                      {statusLabel[job.status] || job.status}
                    </span>
                  </div>
                  {pct !== null && p && (
                    <>
                      <div className="mt-2"><ProgressBar pct={pct} /></div>
                      <p className="text-xs text-gray-500 mt-1">
                        {pct === 0 ? 'Getting started' : pct === 100 ? 'All steps finished' : `${pct}% done`}
                        {' — '}{Number(p.steps_done)} of {Number(p.steps_total)} steps finished
                        {Number(p.steps_active) > 0 ? `, ${Number(p.steps_active)} under way` : ''}
                      </p>
                    </>
                  )}
                  {p?.next_step_title && (
                    <p className="text-xs text-gray-500 mt-0.5">Up next: {p.next_step_title}</p>
                  )}
                  <p className="text-xs text-[#C77D3F] font-semibold mt-1">See photos &amp; progress →</p>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* What's happened lately — everything the office has shared, newest first */}
      {activity.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">history</span>
            What's happened lately
          </h2>
          <p className="text-xs text-gray-500 mb-3">Everything new on your projects, newest first.</p>
          <div className="space-y-3">
            {activity.slice(0, 8).map((a, i) => {
              const meta = ACTIVITY_META[a.kind];
              const inner = (
                <div className="flex gap-3">
                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${meta.tint}`}>
                    <span className="material-symbols-outlined text-lg">{meta.icon}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{activityHeadline(a)}</p>
                    {a.kind === 'log' && a.body && (
                      <p className="text-xs text-gray-600 mt-0.5 leading-relaxed line-clamp-3">{a.body}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {a.date ? whenWords(a.date) : ''}{a.job_title ? ` · ${a.job_title}` : ''}
                    </p>
                  </div>
                </div>
              );
              const to = a.kind === 'invoice' ? `/portal/client/invoices/${a.ref_id}`
                : a.kind === 'quote' ? `/portal/client/quotes/${a.ref_id}`
                : a.job_id ? `/portal/client/jobs/${a.job_id}` : null;
              return to
                ? <Link key={`${a.kind}-${a.ref_id}-${i}`} to={to} className="block -mx-2 px-2 py-1 rounded-lg active:bg-[#FAF6F0]">{inner}</Link>
                : <div key={`${a.kind}-${a.ref_id}-${i}`} className="px-0 py-1">{inner}</div>;
            })}
          </div>
        </div>
      )}

      {/* Site photos teaser — only when the dated progress reports above aren't
          already showing these same photos, so nothing appears twice. */}
      {progress.length === 0 && sitePhotos.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">photo_library</span>
            Photos from your site
          </h2>
          <p className="text-xs text-gray-500 mb-3">The latest pictures our crew took. Tap any photo to open your project.</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {sitePhotos.map(ph => (
              <Link key={ph.id} to={`/portal/client/jobs/${ph.job_id}`} className="flex-shrink-0">
                <img src={ph.url} alt={ph.caption || 'Site photo'} loading="lazy"
                  className="h-24 w-24 rounded-xl object-cover border border-gray-100" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Outstanding invoices — #12 one-tap Pay */}
      {outstanding.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h2 className="font-semibold text-amber-800 mb-2 flex items-center gap-2 text-sm">
            <span className="material-symbols-outlined text-amber-600 text-lg">receipt_long</span>
            Outstanding Invoices
          </h2>
          {payError && <p className="text-xs text-red-600 mb-2">{payError}</p>}
          <div className="space-y-3">
            {outstanding.slice(0, 3).map(inv => (
              <div key={inv.id} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-amber-900">Invoice #{inv.invoice_number}</p>
                  {inv.due_date && (
                    <p className="text-xs text-amber-600">
                      Due {new Date(inv.due_date + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className="font-bold text-amber-800 text-sm">{fmt(inv.balance_due)}</p>
                  {/* #12 – one-tap Pay button on each outstanding invoice */}
                  {cardPayOn && <button
                    onClick={() => handleQuickPay(inv)}
                    disabled={paying === inv.id}
                    className="text-xs font-semibold px-3 py-1.5 bg-[#C77D3F] text-white rounded-lg hover:bg-[#b36e35] disabled:opacity-50 transition-colors active:scale-95"
                  >
                    {paying === inv.id ? '…' : 'Pay'}
                  </button>}
                </div>
              </div>
            ))}
          </div>
          <Link to="/portal/client/invoices"
            className="mt-3 flex items-center justify-center gap-1 w-full py-2 bg-white border border-amber-300 text-amber-700 rounded-lg text-sm font-semibold hover:bg-amber-50 transition-colors">
            All Invoices
            <span className="material-symbols-outlined text-base">arrow_forward</span>
          </Link>
        </div>
      )}

      {/* #17 – tap-to-call/email PM + #20 quick message sheet */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
          <span className="material-symbols-outlined text-[#C77D3F] text-lg">support_agent</span>
          Contact OchrePoint
        </h2>
        <div className="flex gap-2">
          <a href={`tel:${OP_PM_PHONE}`}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:border-[#C77D3F] hover:text-[#C77D3F] transition-colors active:scale-95">
            <span className="material-symbols-outlined text-base">phone</span>
            Call
          </a>
          <a href={`mailto:${OP_PM_EMAIL}`}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:border-[#C77D3F] hover:text-[#C77D3F] transition-colors active:scale-95">
            <span className="material-symbols-outlined text-base">mail</span>
            Email
          </a>
          {/* #20 – quick message / request button */}
          <button
            onClick={() => { setMsgSheetOpen(true); setMsgSent(false); }}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:border-[#C77D3F] hover:text-[#C77D3F] transition-colors active:scale-95"
          >
            <span className="material-symbols-outlined text-base">chat</span>
            Message
          </button>
        </div>
      </div>

      {/* #20 – quick message bottom sheet */}
      <BottomSheet
        isOpen={msgSheetOpen}
        onClose={() => setMsgSheetOpen(false)}
        title="Send a Message"
      >
        {msgSent ? (
          <div className="py-8 flex flex-col items-center gap-3">
            <span className="material-symbols-outlined text-green-500 text-5xl">check_circle</span>
            <p className="text-sm font-semibold text-gray-900">Message sent!</p>
            <p className="text-xs text-gray-500 text-center">We'll get back to you soon.</p>
            <button onClick={() => setMsgSheetOpen(false)}
              className="mt-2 px-6 py-2 bg-[#C77D3F] text-white rounded-xl text-sm font-semibold">
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4 pb-2">
            <p className="text-sm text-gray-500">Send a quick note or request — it lands in our inbox and on our dashboard right away.</p>
            {msgError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{msgError}</p>
            )}
            <textarea
              ref={msgRef}
              value={msgText}
              onChange={e => setMsgText(e.target.value)}
              placeholder="e.g. Can we move next week's visit to Thursday?"
              rows={4}
              autoFocus
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40 resize-none"
            />
            <button
              onClick={sendMessage}
              disabled={msgBusy || !msgText.trim()}
              className="w-full py-3 bg-[#C77D3F] text-white rounded-xl text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50 active:scale-95 transition-transform"
            >
              {msgBusy ? 'Sending…' : 'Send Message'}
            </button>
          </div>
        )}
      </BottomSheet>

      {/* Quick links */}
      <div className="grid grid-cols-3 gap-3">
        <Link to="/portal/client/invoices" className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center gap-2 hover:bg-[#FAF6F0] transition-colors active:scale-95">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-blue-600 text-xl">receipt_long</span>
          </div>
          <span className="text-xs font-semibold text-gray-700 text-center">Invoices</span>
        </Link>
        <Link to="/portal/client/documents" className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center gap-2 hover:bg-[#FAF6F0] transition-colors active:scale-95">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-amber-600 text-xl">folder</span>
          </div>
          <span className="text-xs font-semibold text-gray-700 text-center">Documents</span>
        </Link>
        <Link to="/portal/client/schedule" className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center gap-2 hover:bg-[#FAF6F0] transition-colors active:scale-95">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-green-600 text-xl">calendar_month</span>
          </div>
          <span className="text-xs font-semibold text-gray-700 text-center">Schedule</span>
        </Link>
      </div>
    </div>
    </PullToRefresh>
  );
};
